<script lang="ts">
	import Header from '$lib/Header.svelte';
	import AudioShowCaseCard from './AudioShowCaseCard.svelte';
	import LandingPageHeader from './LandingPageHeader.svelte';
	import LandingPageText from './LandingPageText.svelte';
	import heroSectionBackground from '$lib/assets/images/hero-section-background.png?enhanced';
	import araShowcaseVideoThumbnail from '$lib/assets/images/ara-showcase-video-thumbnail.png?w=1080&format=jpeg&quality=25';
	import captureShowcaseVideoThumbnail from '$lib/assets/images/capture-showcase-video-thumbnail.png?w=1080&format=jpeg&quality=25';
	import flStudioTutorialThumbnail from '$lib/assets/images/fl-studio-tutorial-thumbnail.png?w=1080&format=jpeg&quality=25';
	import abletonTutorialThumbnail from '$lib/assets/images/ableton-tutorial-thumbnail.png?w=1080&format=jpeg&quality=25';
	import openGraphDefault from '$lib/assets/images/opengraph-default.png?lazy';
	import openGraphTwitter from '$lib/assets/images/opengraph-twitter.png?lazy';
	import flTutorial from '$lib/assets/videos/fl-studio-tutorial-1.mp4';
	import abletonTutorial from '$lib/assets/videos/ableton-tutorial-1.mp4';
	import araShowcaseVideo from '$lib/assets/videos/ara-showcase-video-web.mp4';
	import captureShowcaseVideo from '$lib/assets/videos/capture-showcase-video-web.mp4';
	import LandingPageSection from './LandingPageSection.svelte';
	import { Accordion } from 'bits-ui';
	import AccordionItem from './AccordionItem.svelte';
	import AOS from 'aos';
	import emblaCarouselSvelte, { type EmblaCarouselSvelteType } from 'embla-carousel-svelte';
	import FadePlugin from 'embla-carousel-fade';
	import 'aos/dist/aos.css';
	import { getContext, onMount, tick } from 'svelte';
	import Testimonial from './Testimonial.svelte';
	import { ChevronLeftIcon, ChevronRightIcon, ShieldCheck } from '@lucide/svelte';
	import _ from 'lodash';
	import PricingCard from './PricingCard.svelte';
	import { Button } from '$lib/shadcn/components/ui/button';
	import Input from '$lib/shadcn/components/ui/input/input.svelte';
	import { Checkbox } from '$lib/shadcn/components/ui/checkbox';
	import { Textarea } from '$lib/shadcn/components/ui/textarea';
	import { serviceContext } from '$lib/ServiceContext';
	import { SignInState } from '$lib/AuthService.svelte';
	import Progress from '$lib/shadcn/components/ui/progress/progress.svelte';
	import { PUBLIC_VOICE_ALIGN_WEBSITE } from '$env/static/public';
	import Badge from '$lib/shadcn/components/ui/badge/badge.svelte';
	import { cn } from '$lib/shadcn/utils';
	import { MediaQuery } from 'svelte/reactivity';
	import { fetchRetry } from '$lib/utils';
	import { pricing } from '$lib/pricing';
	import SvelteSeo from 'svelte-seo';
	import { resolveLocale, useToast } from '$lib/utils.svelte';
	import { page } from '$app/state';

	let newsletterEmail = $state('');
	let contactformEmail = $state('');
	let contactformName = $state('');
	let contactformMessage = $state('');
	let contactformAgree = $state(false);
	let faqAccordionValues = $state<string[]>([]);

	const showPurchases = true;

	let mounted = $state(false);
	let initialLoad = getContext<{ value: boolean }>('initialLoad');

	async function submitNewsletterSubscription(_e: SubmitEvent) {
		const subscribing = fetchRetry.json(
			'/api/newsletter/subscribe',
			{ email: newsletterEmail, my_value: 'please_dont_abuse' },
			{
				method: 'POST'
			}
		);
		(await useToast()).promise(subscribing, {
			loading: 'Subscribing to newsletter...',
			success: 'Subscribed to newsletter',
			error: 'Failed to subscribe to newsletter'
		});
		await subscribing;
		newsletterEmail = '';
	}

	let first_video = $state<HTMLVideoElement>();
	let second_video = $state<HTMLVideoElement>();
	let firstVideoProgress = $state(0);
	let secondVideoProgress = $state(0);
	let maxMd = new MediaQuery('width < 48rem');

	async function submitContactform(_e: SubmitEvent) {
		const submitting = fetchRetry.json(
			'/api/contact_form/add',
			{
				email: contactformEmail,
				name: contactformName,
				message: contactformMessage,
				agree: contactformAgree,
				my_value: 'please_dont_abuse'
			},
			{
				method: 'POST'
			}
		);

		(await useToast()).promise(submitting, {
			loading: 'Submitting message...',
			success: 'Message submitted',
			error: 'Failed to submit message'
		});
		await submitting;
		contactformEmail = '';
		contactformName = '';
		contactformMessage = '';
		contactformAgree = false;
	}

	onMount(async () => {
		// Load with offset 0, to prevent flickering out elements that have already been shown.
		AOS.init({
			once: true,
			useClassNames: true,
			easing: 'ease-out-quad',
			duration: 400,
			offset: 0
		});
		// Then reset to default of 120px for better experience
		document
			.querySelectorAll('[data-aos]')
			.forEach((elem) => elem.setAttribute('data-aos-offset', '120'));
		const hash = window.location.hash.slice(1);
		if (!_.isEmpty(hash)) {
			faqAccordionValues.push(hash);
			await tick();
			document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}

		mounted = true;
	});

	onMount(() => {
		// In case the first video ended we have to invoke the ended callback here,
		// otherwise the second video will never play
		if (first_video?.ended) {
			document.querySelector('#first_video')?.classList.remove('flipInX');
			document.querySelector('#first_video')?.classList.add('fadeOut');
			document.querySelector('#second_video')?.classList.remove('fadeOut');
			document.querySelector('#second_video')?.classList.add('flipInX');
			second_video?.play();
		}
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let emblaApi: any = undefined;
	function onInit(event: CustomEvent<EmblaCarouselSvelteType>) {
		emblaApi = event.detail;
	}
</script>

<SvelteSeo
	title="TightStacks - Vocal Alignment Plugin for Doubles & Backing Vocals"
	description="TightStacks is an audio plugin and web app to automatically tighten doubles, stacks and backing vocals to your reference track."
	openGraph={{
		site_name: 'TightStacks',
		url: PUBLIC_VOICE_ALIGN_WEBSITE,
		images: [{ url: PUBLIC_VOICE_ALIGN_WEBSITE + openGraphDefault }]
	}}
	twitter={{
		card: 'summary_large_image',
		image: PUBLIC_VOICE_ALIGN_WEBSITE + openGraphTwitter
	}}
	jsonLd={{
		'@context': 'https://schema.org',
		// @wc-ignore
		'@graph': [
			{
				'@type': 'WebSite',
				name: 'TightStacks',
				url: PUBLIC_VOICE_ALIGN_WEBSITE
			},
			{
				'@type': 'Organization',
				name: 'TightStacks',
				url: PUBLIC_VOICE_ALIGN_WEBSITE,
				logo: PUBLIC_VOICE_ALIGN_WEBSITE + '/web-app-manifest-1024x1024.png'
			},
			{
				'@type': 'SoftwareApplication',
				name: 'TightStacks',
				applicationCategory: 'MultimediaApplication',
				applicationSubCategory: 'AudioPlugin',
				operatingSystem: ['macOS', 'Windows'],
				// @wc-include
				description:
					'TightStacks is a vocal alignment VST plugin that automatically tightens doubles, stacks, and backing vocals. Currently free during early access. A free web app version is also available.',
				offers: [
					{
						'@type': 'Offer',
						price: `${pricing.lifetime.price}.${pricing.lifetime.priceCents ?? '00'}`,
						priceCurrency: 'EUR',
						itemCondition: 'https://schema.org/NewCondition',
						availability: 'https://schema.org/InStock'
					},
					{
						'@type': 'Offer',
						price: `${pricing.monthly.price}.${pricing.monthly.priceCents ?? '00'}`,
						priceCurrency: 'EUR',
						priceSpecification: {
							'@type': 'UnitPriceSpecification',
							billingIncrement: 1,
							priceUnit: 'MONTH',
							price: '1'
						},
						itemCondition: 'https://schema.org/NewCondition',
						availability: 'https://schema.org/InStock'
					}
				]
			}
		]
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any}
/>
<svelte:head>
	<link rel="preload" as="image" href={araShowcaseVideoThumbnail} fetchpriority="high" />
</svelte:head>

<div class="absolute top-25 left-0 -z-1 w-screen overflow-x-hidden sm:top-15 md:top-0">
	<enhanced:img
		fetchpriority="high"
		alt="background-texture"
		src={heroSectionBackground}
		class="w-[150vw] origin-top-left opacity-20 blur-xs"
	/>
</div>
<Header showNav showPricing={showPurchases} blurIn={initialLoad.value} />
<main
	class="flex flex-col items-stretch gap-40 scroll-smooth py-20 **:scroll-mt-40 md:gap-40 lg:gap-60 lg:py-24"
>
	<div class="flex flex-col gap-40 md:gap-0">
		<div class="relative flex flex-col md:min-h-[calc(100vh-4rem)]">
			<LandingPageSection class="" containerClasses="flex items-stretch flex-col" skipAnimation>
				<div
					class="*:blurIn col-span-2 flex flex-col items-stretch gap-6 lg:max-w-160 lg:items-start"
				>
					<h1 class="h1 leading-none tracking-normal drop-shadow">
						Align vocal doubles in <br class="hidden sm:inline" />
						<span class="text-primary">seconds, not hours</span>
					</h1>
					<h2 class="h2 base-font-family text-muted-foreground text-sm tracking-normal delay-150">
						TightStacks automatically tightens doubles, stacks and backing vocals to your reference
						track. Allowing you to focus on your creative work instead.
					</h2>
					<div class="mt-4 flex flex-col items-center gap-2 delay-300 sm:flex-row">
						<a
							href={resolveLocale('/(webapp)/[[locale=locale]]/download')}
							class="w-full lg:w-60"
							data-sveltekit-preload-code="eager"
						>
							<Button variant="filled" size="xl" type="button" class="w-full shadow"
								>Get Plugin</Button
							>
						</a>
						<a
							href={resolveLocale('/(webapp)/[[locale=locale]]/app')}
							class="text-muted-foreground-2 w-full lg:w-60"
						>
							<Button variant="ghost2" size="lg" type="button" class={cn('w-full')}
								>Try in Browser →</Button
							>
						</a>
					</div>
					<div class="mt-4 flex gap-2 delay-450">
						<!-- @wc-ignore -->
						<Badge variant="outline">Windows</Badge>
						<!-- @wc-ignore -->
						<Badge variant="outline">macOS</Badge>
						<Badge variant="outline">1k+ downloads</Badge>
					</div>
				</div>
			</LandingPageSection>
		</div>
		<LandingPageSection
			containerClasses="flex items-stretch flex-col gap-8"
			skipAnimation={maxMd.current}
			class="max-md:blurIn max-md:delay-1000"
		>
			<div class="grid">
				<div
					id="second_video"
					class="from-primary to-secondary col-start-1 row-start-1 overflow-hidden rounded-md bg-linear-to-b p-1 shadow-xl shadow-black/50 md:rounded-xl md:p-2"
				>
					<video
						src={captureShowcaseVideo}
						disablepictureinpicture
						preload="auto"
						muted
						aria-label="short showcase video"
						poster={captureShowcaseVideoThumbnail}
						bind:this={second_video}
						ontimeupdate={(_e) =>
							(secondVideoProgress =
								(second_video?.currentTime ?? 0) / (second_video?.duration ?? 1))}
						onended={() => {
							document.querySelector('#second_video')?.classList.remove('flipInX');
							document.querySelector('#second_video')?.classList.add('fadeOut');
							document.querySelector('#first_video')?.classList.remove('fadeOut');
							document.querySelector('#first_video')?.classList.add('flipInX');
							first_video?.play();
						}}
						class="aos-delay aspect-16/10 w-full bg-none"
					>
					</video>
					<Progress class="h-0.5 rounded-none md:h-1" value={secondVideoProgress} max={1} />
				</div>
				<div
					id="first_video"
					class="from-primary to-secondary col-start-1 row-start-1 overflow-hidden rounded-md bg-linear-to-b p-1 shadow-xl shadow-black/50 md:rounded-xl md:p-2"
				>
					<div class="bg-shade-primary/90 animate-shimmer rounded-md">
						<video
							src={araShowcaseVideo}
							disablepictureinpicture
							preload="auto"
							// @ts-expect-error attribute actually exists
							loading="eager"
							muted
							autoplay
							aria-label="short showcase video"
							poster={araShowcaseVideoThumbnail}
							bind:this={first_video}
							ontimeupdate={(_e) =>
								(firstVideoProgress =
									(first_video?.currentTime ?? 0) / (first_video?.duration ?? 1))}
							onended={() => {
								document.querySelector('#first_video')?.classList.remove('flipInX');
								document.querySelector('#first_video')?.classList.add('fadeOut');
								document.querySelector('#second_video')?.classList.remove('fadeOut');
								document.querySelector('#second_video')?.classList.add('flipInX');
								second_video?.play();
							}}
							class="aos-delay relative z-101 aspect-16/10 w-full bg-none"
						>
						</video>
					</div>
					<Progress class="h-0.5 rounded-none md:h-1" value={firstVideoProgress} max={1} />
				</div>
			</div>
		</LandingPageSection>
	</div>
	<LandingPageSection class="" id="examples">
		<div class="flex flex-col gap-4 rounded-xl">
			<div class="mb-8 flex flex-col">
				<LandingPageHeader>Hear the difference instantly</LandingPageHeader>
				<LandingPageText
					>TightStacks automatically locks your takes together. No more manual slicing, nudging and
					guesswork.</LandingPageText
				>
			</div>
			<AudioShowCaseCard
				urlBefore="/sounds/open_your_eyes_before.mp3"
				urlAfter="/sounds/open_your_eyes_after.mp3"
			/>
			<AudioShowCaseCard
				urlBefore="/sounds/simulations_before.mp3"
				urlAfter="/sounds/simulations_after.mp3"
			/>
		</div>
	</LandingPageSection>
	<LandingPageSection id="usage">
		<div class="flex flex-col gap-4 rounded-xl">
			<div class="mb-8 flex flex-col">
				<LandingPageHeader>Use with ARA compatible DAWs ...</LandingPageHeader>
				<!-- @wc-ignore -->
				<LandingPageText>Reaper, Cubase, ...</LandingPageText>
			</div>
			<div class="flex flex-col gap-2 rounded-xl">
				<div class="border-foreground/5 flex flex-col justify-between gap-4 rounded-xl lg:flex-row">
					<div class="flex flex-col gap-2">
						<div class="preset-tonal-outlined flex gap-2 rounded-md p-2 shadow">
							<div>1</div>
							<div>Load plugin onto tracks</div>
						</div>
						<div class="preset-tonal-outlined flex gap-2 rounded-md p-2 shadow">
							<div>2</div>
							<div>Assign reference and double (dub) tracks</div>
						</div>
						<div class="preset-tonal-outlined flex gap-2 rounded-md p-2 shadow">
							<div>3</div>
							<div>Wait for alignment</div>
						</div>
					</div>
					<div
						class="preset-tonal w-full shrink-0 overflow-hidden rounded-xl shadow lg:w-120 xl:w-180 2xl:w-200"
					>
						<video
							src={mounted ? araShowcaseVideo : undefined}
							poster={araShowcaseVideoThumbnail}
							disablepictureinpicture
							preload={mounted ? 'auto' : 'none'}
							muted
							controls
							aria-label="short showcase video"
							class="aspect-16/10 w-full"
						>
						</video>
					</div>
				</div>
			</div>
		</div>
	</LandingPageSection>
	<LandingPageSection>
		<div class="flex flex-col gap-4 rounded-xl">
			<div class="mb-8 flex flex-col">
				<LandingPageHeader>... and all other popular DAWs</LandingPageHeader>
				<!-- @wc-ignore -->
				<LandingPageText>FL Studio, Ableton Live, ...</LandingPageText>
			</div>
			<div class="flex flex-col gap-2 rounded-xl">
				<div class="border-foreground/5 flex flex-col justify-between gap-4 rounded-xl lg:flex-row">
					<div class="flex flex-col gap-2">
						<div class="preset-tonal-outlined flex gap-2 rounded-md p-2 shadow">
							<div>1</div>
							<div>Load plugin onto double track</div>
						</div>
						<div class="preset-tonal-outlined flex gap-2 rounded-md p-2 shadow">
							<div>2</div>
							<div>Sidechain reference track to double track</div>
						</div>
						<div class="preset-tonal-outlined flex gap-2 rounded-md p-2 shadow">
							<div>3</div>
							<div>Capture audio</div>
						</div>
						<div class="preset-tonal-outlined flex gap-2 rounded-md p-2 shadow">
							<div>4</div>
							<div>Wait for alignment</div>
						</div>
					</div>
					<div
						class="preset-tonal w-full shrink-0 overflow-hidden rounded-xl shadow lg:w-120 xl:w-180 2xl:w-200"
					>
						<video
							src={mounted ? flTutorial : undefined}
							poster={flStudioTutorialThumbnail}
							disablepictureinpicture
							preload={mounted ? 'auto' : 'none'}
							muted
							controls
							aria-label="short showcase video"
							class="aspect-16/10 w-full"
						>
						</video>
					</div>
				</div>
			</div>
		</div>
	</LandingPageSection>
	<LandingPageSection containerClasses="flex items-stretch flex-col">
		<div class="flex flex-col items-stretch gap-4">
			<LandingPageHeader>Feedback from early access users</LandingPageHeader>
			<div class="embla flex max-w-full flex-col items-stretch gap-2 self-start lg:max-w-110">
				<div
					class="embla__viewport -m-2 overflow-hidden p-2"
					use:emblaCarouselSvelte={{
						options: { loop: true, containScroll: false },
						plugins: [FadePlugin()]
					}}
					onemblaInit={onInit}
				>
					<!-- @wc-ignore -->
					<div class="embla__container flex gap-8">
						<Testimonial
							name="Dillon Moses, Sound Engineer"
							text="This saved my project. I found it and finished the album the same day. An unbelievable tool."
						/>
						<Testimonial
							name="RXSound"
							text="Exactly the kind of simple, smart, and efficient vocal alignment tool I've been wishing for &mdash; It's a real breath of fresh air in vocal editing."
						/>
					</div>
				</div>
				<div class="flex justify-end gap-2 lg:justify-start">
					<Button
						variant="ghost3"
						size="icon-sm"
						onclick={() => emblaApi?.scrollPrev()}
						class="embla__prev"><ChevronLeftIcon /></Button
					>
					<Button
						variant="ghost3"
						size="icon-sm"
						onclick={() => emblaApi?.scrollNext()}
						class="embla__next"><ChevronRightIcon /></Button
					>
				</div>
			</div>
		</div>
	</LandingPageSection>
	<div class="flex flex-col gap-20">
		{#if showPurchases}
			<LandingPageSection
				class="preset-tonal py-8"
				containerClasses="flex items-center flex-col"
				id="pricing"
			>
				<div class="flex flex-col items-center justify-center gap-4">
					<div class="mb-8 flex flex-col text-center">
						<LandingPageHeader>Pricing</LandingPageHeader>
						<LandingPageText
							>TightStacks is a paid plugin. It is available as a one time purchase or as a flexible
							subscription. Choose what fits you best.</LandingPageText
						>
					</div>
					<div class="flex flex-col items-center justify-center gap-4 lg:flex-row">
						<PricingCard
							highlighted
							title="Lifetime"
							description="Provides lifetime access to the TightStacks plugin with a single purchase and no recurring costs."
							price={pricing.lifetime.price}
							priceCents={pricing.lifetime.priceCents}
							fullPrice={pricing.lifetime.fullPrice}
							fullPriceCents={pricing.lifetime.fullPriceCents}
							salePercentage={pricing.lifetime.salePercentage}
							duration="/ once"
							purchaseId={pricing.lifetime.purchaseId}
							purchaseMode="payment"
							buttonText="Purchase"
							checkItems={['Full access', 'All updates', 'No subscription']}
							returnUrl={page.url.pathname + '#pricing'}
							successUrl={resolveLocale('/(webapp)/[[locale=locale]]/checkout/success')}
							onclick={() => serviceContext.analyticsService.initiatePurchase('Lifetime', 20)}
						/>
						<PricingCard
							title="Subscription"
							description="Provides flexible access to the TightStacks plugin based on a monthly recurring subscription."
							price={pricing.monthly.price}
							priceCents={pricing.monthly.priceCents}
							fullPrice={pricing.monthly.fullPrice}
							fullPriceCents={pricing.monthly.fullPriceCents}
							salePercentage={pricing.monthly.salePercentage}
							duration="/ month"
							purchaseId={pricing.monthly.purchaseId}
							purchaseMode="subscription"
							buttonText="Subscribe"
							checkItems={['Full access', 'All updates', 'Cancel anytime']}
							returnUrl={page.url.pathname + '#pricing'}
							successUrl={resolveLocale('/(webapp)/[[locale=locale]]/checkout/success')}
							onclick={() => serviceContext.analyticsService.initiatePurchase('Subscription', 1)}
						/>
					</div>
					<LandingPageText
						class="text-muted-foreground-2 lg:text-md flex items-center gap-2 text-xs"
						><ShieldCheck class="size-3 lg:size-4" /> All Licenses come with a 30 day money back guarantee.</LandingPageText
					>
				</div>
			</LandingPageSection>
		{/if}
		<LandingPageSection containerClasses="flex justify-center">
			<div
				class="border-primary card flex w-full flex-col gap-4 border p-4 shadow md:flex-row md:items-center lg:max-w-(--breakpoint-md)"
			>
				<div class="flex grow flex-col md:max-w-1/2">
					<LandingPageHeader>Try it now</LandingPageHeader>
					<LandingPageText
						>The plugin is available for Windows (VST3) and macOS (VST3/AU).</LandingPageText
					>
				</div>
				<Button
					variant="default"
					size="lg"
					class="grow"
					href={resolveLocale('/(webapp)/[[locale=locale]]/download')}>Get Plugin</Button
				>
			</div>
		</LandingPageSection>
		<LandingPageSection containerClasses="flex justify-center">
			<div
				class="preset-tonal-outlined card flex w-full flex-col gap-4 p-4 shadow md:flex-row md:items-center lg:max-w-(--breakpoint-md)"
			>
				<div class="flex grow flex-col md:max-w-1/2">
					<LandingPageHeader>Stay up to date!</LandingPageHeader>
					<LandingPageText
						>Subscribe to stay informed about new features and offers.</LandingPageText
					>
				</div>
				<form
					class="flex grow items-stretch gap-2"
					onsubmit={(e) => {
						e.preventDefault();
						submitNewsletterSubscription(e);
						return false;
					}}
				>
					<Input
						bind:value={newsletterEmail}
						id="newsletter_email"
						variant="tonal"
						type="email"
						class="w-full"
						placeholder="Email"
					/>
					<Button type="submit" variant="tonal" class="flex-1">Subscribe</Button>
				</form>
			</div>
		</LandingPageSection>
		<LandingPageSection containerClasses="flex justify-center">
			<div
				class="preset-tonal-outlined card flex w-full flex-col items-stretch gap-4 p-4 shadow lg:max-w-(--breakpoint-md)"
				id="contact_form"
			>
				<LandingPageHeader class="text-center">Get in touch</LandingPageHeader>
				<form
					class="flex flex-col gap-4"
					onsubmit={(e) => {
						e.preventDefault();
						submitContactform(e);
						return false;
					}}
				>
					<Input
						bind:value={contactformName}
						variant="tonal"
						id="contactform_name"
						type="text"
						class="input preset-tonal"
						placeholder="Name"
					/>
					<Input
						bind:value={contactformEmail}
						type="email"
						id="contactform_email"
						variant="tonal"
						class="input preset-tonal"
						placeholder="Email"
					/>
					<Textarea
						bind:value={contactformMessage}
						id="contactform_message"
						class="textarea preset-tonal"
						variant="tonal"
						placeholder="Message"
					></Textarea>
					<Checkbox
						bind:checked={contactformAgree}
						id="contactform_agree"
						class="checkbox preset-tonal"
						hidden
					/>
					<Button variant="tonal" type="submit">Send</Button>
				</form>
			</div>
		</LandingPageSection>
		<LandingPageSection containerClasses="flex justify-center" id="faq">
			<div
				class="preset-tonal-outlined card flex w-full flex-col items-stretch gap-4 p-4 shadow lg:max-w-(--breakpoint-md)"
			>
				<LandingPageHeader class="text-center">FAQ</LandingPageHeader>
				<Accordion.Root type="multiple" class="flex flex-col gap-4" bind:value={faqAccordionValues}>
					<AccordionItem title="Which DAWs are supported?">
						<div class="flex w-full flex-col gap-2 lg:flex-row lg:gap-8">
							<div>
								<p class="font-semibold">TightStacks supports the following DAWs:</p>
								<!-- @wc-ignore -->
								<ul class="list-inside list-disc">
									<li>Ableton</li>
									<li>FL Studio</li>
									<li>Logic Pro</li>
									<li>Studio One</li>
									<li>Reaper</li>
									<li>Cubase</li>
									<li>GarageBand</li>
									<li>...</li>
								</ul>
							</div>
							<div>
								<p class="font-semibold">The following DAWs are not supported:</p>
								<ul class="list-inside list-disc">
									<!-- @wc-ignore -->
									<li>Pro Tools</li>
								</ul>
							</div>
						</div>
					</AccordionItem>
					<hr class="hr" />
					<AccordionItem title="How to use TightStacks with FL Studio?" value="faq_fl_tutorial">
						<div class="flex flex-col gap-2">
							<p>
								TightStacks needs to be set up with side chaining. To do this, go into the plugin
								settings and select the correct input busses.
							</p>
							<video
								src={mounted ? flTutorial : undefined}
								poster={flStudioTutorialThumbnail}
								disablepictureinpicture
								preload={mounted ? 'auto' : 'none'}
								muted
								controls
								aria-label="FL Studio TightStacks Tutorial"
								class="aspect-16/10 w-full"
							>
							</video>
						</div>
					</AccordionItem>
					<hr class="hr" />
					<AccordionItem title="How to use TightStacks with Ableton Live?" value="faq_ableton_tutorial">
						<div class="flex flex-col gap-2">
							<p>
								TightStacks needs to be set up with side chaining. To do this, select the sidechain
								source in the plugin.
							</p>
							<video
								src={mounted ? abletonTutorial : undefined}
								poster={abletonTutorialThumbnail}
								disablepictureinpicture
								preload={mounted ? 'auto' : 'none'}
								muted
								controls
								aria-label="Ableton TightStacks Tutorial"
								class="aspect-16/10 w-full"
							>
							</video>
						</div>
					</AccordionItem>
					<hr class="hr" />
					<AccordionItem title="Is TightStacks an alternative to VocAlign, ReVoice Pro or Sync Vx?">
						<div class="flex flex-col gap-2">
							<p>
								Yes. TightStacks provides an affordable, streamlined alternative workflow for
								automated vocal alignment. It supports both ARA2-compatible hosts (like Reaper and
								Cubase) for instant capture and sidechain processing for other popular DAWs (like FL
								Studio and Ableton Live).
							</p>
						</div>
					</AccordionItem>
					<hr class="hr" />
					<AccordionItem title="Is TightStacks free to use?">
						No, TightStacks used to be free while still in early access. Now it requires a paid
						license.
					</AccordionItem>
					<hr class="hr" />
					<AccordionItem title="How do I uninstall TightStacks?">
						<div class="flex flex-col gap-2">
							<p>TightStacks can be removed by deleting the plugin files from your system.</p>
							<div>
								<!-- @wc-ignore -->
								<p class="font-semibold">macOS:</p>
								<!-- @wc-ignore -->
								<ul class="list-inside list-disc">
									<li>AU: /Library/Audio/Plug-Ins/Components/TightStacks.component</li>
									<li>VST3: /Library/Audio/Plug-Ins/VST3/TightStacks.vst3</li>
								</ul>
								<p>Note: These locations may be hidden by default</p>
							</div>
							<div>
								<!-- @wc-ignore -->
								<p class="font-semibold">Windows:</p>
								<!-- @wc-ignore -->
								<ul class="list-inside list-disc">
									<li>VST3: C:\Program Files\Common Files\VST3 or user defined path</li>
								</ul>
							</div>
							<p>Removing the plugin will not affect your audio files or projects.</p>
						</div>
					</AccordionItem>
					<hr class="hr" />
					<AccordionItem title="How can I cancel my subscription?">
						<div class="flex flex-col gap-2">
							<p>
								You can always cancel your subscription by going into your Account page. The Account
								page is located on the top right under your avatar.
							</p>
							{#if serviceContext.authService.getSignInState() == SignInState.SignedIn}
								<p>
									Or just click <a
										href={resolveLocale('/(webapp)/[[locale=locale]]/account')}
										class="anchor">here</a
									> to go there directly.
								</p>
							{/if}
						</div>
					</AccordionItem>
					<hr class="hr" />
					<AccordionItem title="How can I refund my purchase?">
						<div class="flex flex-col gap-2">
							<p>
								If you purchased TightStacks in the last 30 days and changed your mind, you are
								entitled to a full refund.
							</p>
							<p>
								Request your refund by going into your Account page. The Account page is located on
								the top right under your avatar.
							</p>
							{#if serviceContext.authService.getSignInState() == SignInState.SignedIn}
								<p>
									Or just click <a
										href={resolveLocale('/(webapp)/[[locale=locale]]/account')}
										class="anchor">here</a
									> to go there directly.
								</p>
							{/if}
							<p>
								Note: If you purchased the subscription instead, just send me a message using the
								contact form for your refund.
							</p>
						</div>
					</AccordionItem>
				</Accordion.Root>
			</div>
		</LandingPageSection>
	</div>
</main>
